import React, { useCallback, useEffect, useState } from 'react'
import { FaProjectDiagram, FaRobot, FaBars } from 'react-icons/fa'
import cn from 'classnames'
import { html } from 'lib'
import ShapeMenu from 'components/graph/controls/shapeMenu'
import GraphEditMenu from 'components/graph/controls/graphEditMenu'
import ContentMenu from 'components/graph/controls/contentMenu'
import ContextualMenuToggle from 'components/graph/controls/contextualMenuToggle'
import KindControl from 'components/graph/controls/kindControl'
import KindMenu from 'components/graph/controls/kindMenu'
import Save from 'components/graph/controls/save'
import Cancel from 'components/graph/controls/cancel'
import * as SpinnerModule from 'components/utils/Spinner'
import { reset } from 'actions'
import { editOneKind, createOneKind } from 'actions/kinds'
import { editOneNode } from 'actions/nodes'
import { editOneEdge } from 'actions/edges'
import { openContentGenerationModal, cancelContentGeneration } from 'actions/contentGeneration'
import { openGraphQueryModal, cancelGraphQuery } from 'actions/graphQuery'
import { addShape, removeShape, updateShape } from 'actions/shapes'
import { createId } from 'lib/id'
import { defaultListSettings, MIN_SPACING, MAX_SPACING, SPACING_STEP } from 'types/shapes'
import type { EdgeDirectionConstraint } from 'types/domain'
import {
  createAndFocusNode,
  createNodeFromActive,
  deleteActive,
  togglePinActive,
  toggleDirectionalityActive,
  startCreatingEdge,
  stopCreatingEdge,
  toggleSelectFocus,
  deselectAll,
  openObjectDetails,
  closeObjectDetails,
} from 'actions/graph'
import selectContentGeneration from 'selectors/contentGeneration'
import selectGraphQuery from 'selectors/graphQuery'
import { selectShapes, selectFocusedShape } from 'selectors/graph/shapes'
import {
  selectCreating,
  selectEdit,
  selectFocus,
  selectFocusedEntity,
  selectSelected,
  selectPinned,
  active,
  selectObjectDetails,
  selectKinds,
} from 'selectors/graph'
import {
  selectCanEditActiveEdges,
  selectCanEditActive,
  selectCanEditFocus,
} from 'selectors/permissions'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import ContentGenerationModal from 'components/ContentGenerationModal'
import GraphQueryModal from 'components/GraphQueryModal'
import 'components/footer.less'

type PointerEventData = {
  clientX?: number
  clientY?: number
  changedTouches?: Array<{ clientX?: number; clientY?: number }>
  preventDefault?: () => void
}

const toHtmlEvent = (e: unknown): Parameters<typeof html.getEventTarget>[0] => {
  const evt = e as PointerEventData
  const touch = evt.changedTouches?.[0]
  const clientX = touch?.clientX ?? evt.clientX ?? 0
  const clientY = touch?.clientY ?? evt.clientY ?? 0
  if (touch) {
    return {
      clientX,
      clientY,
      changedTouches: [{ clientX: touch.clientX ?? clientX, clientY: touch.clientY ?? clientY }],
    }
  }
  return { clientX, clientY }
}

const Spinner = SpinnerModule.default || SpinnerModule

const EDGE_DIRECTION_ORDER: EdgeDirectionConstraint[] = ['free', 'ltr', 'rtl', 'ttb', 'btt']

const Footer = () => {
  const dispatch = useAppDispatch()

  const objectDetails = useAppSelector(selectObjectDetails)
  const creating = useAppSelector(selectCreating)
  const edit = useAppSelector(selectEdit)
  const focus = useAppSelector(selectFocus)
  const selected = useAppSelector(selectSelected)
  const pinned = useAppSelector(selectPinned)
  const activeNodes = useAppSelector(active.nodes)
  const editableActiveEdges = useAppSelector(selectCanEditActiveEdges)
  const editableActive = useAppSelector(selectCanEditActive)
  const editableFocus = useAppSelector(selectCanEditFocus)
  const contentGeneration = useAppSelector((state) => selectContentGeneration(state as any) as any)
  const graphQuery = useAppSelector((state) => selectGraphQuery(state as any) as any)
  const focusedShape = useAppSelector(selectFocusedShape)
  const shapes = useAppSelector(selectShapes)
  const focusedEntity = useAppSelector(selectFocusedEntity)
  const kinds = useAppSelector(selectKinds.all)
  const focusedKind = focusedEntity?.data.kind ? kinds[focusedEntity.data.kind] ?? null : null
  // Only one contextual menu (shape, kind, ...) is open at a time; each reports its own
  // measured width, but all of their toggle buttons stay visible at once.
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [menuWidths, setMenuWidths] = useState<Record<string, number>>({})
  const [addingToShapeId, setAddingToShapeId] = useState<string | null>(null)
  const [addModeStartFocusId, setAddModeStartFocusId] = useState<string | null>(null)

  const openMenuWidth = openMenu ? menuWidths[openMenu] ?? 0 : 0

  const closeMenu = useCallback(() => setOpenMenu(null), [])

  const handleShapeMenuWidthChange = useCallback((width: number) => {
    setMenuWidths((prev) => (prev.shape === width ? prev : { ...prev, shape: width }))
  }, [])

  const handleGraphMenuWidthChange = useCallback((width: number) => {
    setMenuWidths((prev) => (prev.graph === width ? prev : { ...prev, graph: width }))
  }, [])

  const handleContentMenuWidthChange = useCallback((width: number) => {
    setMenuWidths((prev) => (prev.content === width ? prev : { ...prev, content: width }))
  }, [])

  const handleKindMenuWidthChange = useCallback((width: number) => {
    setMenuWidths((prev) => (prev.kind === width ? prev : { ...prev, kind: width }))
  }, [])

  const handleToggleKindMenu = useCallback(() => {
    setOpenMenu((current) => (current === 'kind' ? null : 'kind'))
  }, [])

  const handleChangeKindColor = useCallback((color: string) => {
    if (!focusedKind) return
    const nextStyles = { ...focusedKind.data.styles, color }
    dispatch(editOneKind({ id: focusedKind.id, label: focusedKind.data.label, styles: nextStyles }) as any)
  }, [focusedKind, dispatch])

  const handleToggleEdgeVisible = useCallback(() => {
    if (!focusedKind) return
    const nextStyles = { ...focusedKind.data.styles, edgeVisible: focusedKind.data.styles.edgeVisible === false }
    dispatch(editOneKind({ id: focusedKind.id, label: focusedKind.data.label, styles: nextStyles }) as any)
  }, [focusedKind, dispatch])

  const handleToggleEdgeSpring = useCallback(() => {
    if (!focusedKind) return
    const nextStyles = { ...focusedKind.data.styles, edgeSpring: focusedKind.data.styles.edgeSpring === false }
    dispatch(editOneKind({ id: focusedKind.id, label: focusedKind.data.label, styles: nextStyles }) as any)
  }, [focusedKind, dispatch])

  const handleCycleEdgeDirection = useCallback(() => {
    if (!focusedKind) return
    const current = focusedKind.data.styles.edgeDirection ?? 'free'
    const nextIndex = (EDGE_DIRECTION_ORDER.indexOf(current) + 1) % EDGE_DIRECTION_ORDER.length
    const nextStyles = { ...focusedKind.data.styles, edgeDirection: EDGE_DIRECTION_ORDER[nextIndex] }
    dispatch(editOneKind({ id: focusedKind.id, label: focusedKind.data.label, styles: nextStyles }) as any)
  }, [focusedKind, dispatch])

  const handleToggleEdgeDirectionSoft = useCallback(() => {
    if (!focusedKind) return
    const nextStyles = { ...focusedKind.data.styles, edgeDirectionSoft: !focusedKind.data.styles.edgeDirectionSoft }
    dispatch(editOneKind({ id: focusedKind.id, label: focusedKind.data.label, styles: nextStyles }) as any)
  }, [focusedKind, dispatch])

  const handleChangeEdgeStiffness = useCallback((edgeStiffness: number) => {
    if (!focusedKind) return
    const nextStyles = { ...focusedKind.data.styles, edgeStiffness }
    dispatch(editOneKind({ id: focusedKind.id, label: focusedKind.data.label, styles: nextStyles }) as any)
  }, [focusedKind, dispatch])

  const handleChangeEdgeLength = useCallback((edgeSpringLength: number) => {
    if (!focusedKind) return
    const nextStyles = { ...focusedKind.data.styles, edgeSpringLength }
    dispatch(editOneKind({ id: focusedKind.id, label: focusedKind.data.label, styles: nextStyles }) as any)
  }, [focusedKind, dispatch])

  const handleChangeEdgeThickness = useCallback((edgeThickness: number) => {
    if (!focusedKind) return
    const nextStyles = { ...focusedKind.data.styles, edgeThickness }
    dispatch(editOneKind({ id: focusedKind.id, label: focusedKind.data.label, styles: nextStyles }) as any)
  }, [focusedKind, dispatch])

  const handleSelectKind = useCallback((kindId: string) => {
    if (!focusedEntity) return
    const { id, data } = focusedEntity
    if (focus.isEdge) {
      const directionality = 'directionality' in data ? data.directionality : undefined
      dispatch(editOneEdge({ id, kind: kindId, content: data.content, ...(directionality !== undefined ? { directionality } : {}) }) as any)
    } else {
      dispatch(editOneNode({ id, kind: kindId, content: data.content }) as any)
    }
  }, [focusedEntity, focus.isEdge, dispatch])

  // Mirrors the object-details kind picker's title convention (truncated content + type).
  const rawFocusedContent = focusedEntity?.data.content || ''
  let focusedContentPreview = rawFocusedContent.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
  if (focusedContentPreview.length > 60) {
    focusedContentPreview = focusedContentPreview.slice(0, 60) + '…'
  }
  const kindMenuTitle = `Set Kind for "${focusedContentPreview || '(empty)'}" ${focus.isEdge ? 'edge' : 'node'}`

  // The shape/entity the panel displays: while adding, frozen to the shape the add started on
  // so the panel doesn't reshuffle as focus moves to the entity being added.
  const displayShape = addingToShapeId ? shapes[addingToShapeId] ?? null : focusedShape
  const displayFocusId = addingToShapeId ? addModeStartFocusId ?? focus.id : focus.id

  const isFetchingAny = contentGeneration?.isFetching || graphQuery?.isFetching

  // Finish adding once focus moves to a different entity; adopt it into the shape unless it's already a member.
  useEffect(() => {
    if (!addingToShapeId) return
    if (focus.id === addModeStartFocusId) return
    const shape = shapes[addingToShapeId]
    if (focus.id && shape && !shape.entityIds.includes(focus.id)) {
      dispatch(updateShape(addingToShapeId, { entityIds: [...shape.entityIds, focus.id] }))
    }
    // Syncing local UI state to the external focus change that just completed the add.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAddingToShapeId(null)
  }, [focus.id, addingToShapeId, addModeStartFocusId, shapes, dispatch])

  // Escape cancels adding-to-shape mode.
  useEffect(() => {
    if (!addingToShapeId) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAddingToShapeId(null)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [addingToShapeId])

  // Leaving the shape menu (via any close path) also exits adding-to-shape mode.
  useEffect(() => {
    if (openMenu === 'shape') return
    // Syncing local UI state to the menu-close event that just happened.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAddingToShapeId(null)
  }, [openMenu])

  const handleAddShape = useCallback(() => {
    if (!focus.id || focusedShape) return
    dispatch(addShape({
      id: createId(),
      shapeType: 'list',
      entityIds: [focus.id],
      settings: defaultListSettings,
      layout: true,
    }))
  }, [focus.id, focusedShape, dispatch])

  const handleStartAddToShape = useCallback(() => {
    if (!focusedShape) return
    setAddModeStartFocusId(focus.id)
    setAddingToShapeId(focusedShape.id)
  }, [focusedShape, focus.id])

  const handleCancelAddToShape = useCallback(() => {
    setAddingToShapeId(null)
  }, [])

  const handleToggleShapeMenu = useCallback(() => {
    setOpenMenu((current) => (current === 'shape' ? null : 'shape'))
  }, [])

  const handleToggleGraphMenu = useCallback(() => {
    setOpenMenu((current) => (current === 'graph' ? null : 'graph'))
  }, [])

  const handleToggleContentMenu = useCallback(() => {
    setOpenMenu((current) => (current === 'content' ? null : 'content'))
  }, [])

  const handleRemoveShape = useCallback(() => {
    if (!displayShape) return
    dispatch(removeShape(displayShape.id))
  }, [displayShape, dispatch])

  const handleMoveEntity = useCallback((direction: 'left' | 'right') => {
    if (!displayShape) return
    const index = displayShape.entityIds.indexOf(displayFocusId)
    if (index === -1) return
    const swapIndex = direction === 'left' ? index - 1 : index + 1
    const current = displayShape.entityIds[index]
    const swapTarget = displayShape.entityIds[swapIndex]
    if (swapTarget === undefined || current === undefined) return
    const entityIds = [...displayShape.entityIds]
    entityIds[index] = swapTarget
    entityIds[swapIndex] = current
    dispatch(updateShape(displayShape.id, { entityIds }))
  }, [displayShape, displayFocusId, dispatch])

  const handleRemoveFromShape = useCallback(() => {
    if (!displayShape) return
    const entityIds = displayShape.entityIds.filter((id) => id !== displayFocusId)
    if (entityIds.length === displayShape.entityIds.length) return
    dispatch(updateShape(displayShape.id, { entityIds }))
  }, [displayShape, displayFocusId, dispatch])

  const handleToggleDirection = useCallback(() => {
    if (!displayShape) return
    const direction = displayShape.settings.direction === 'horizontal' ? 'vertical' : 'horizontal'
    dispatch(updateShape(displayShape.id, { settings: { ...displayShape.settings, direction } }))
  }, [displayShape, dispatch])

  const handleAdjustSpacing = useCallback((mode: 'expand' | 'contract') => {
    if (!displayShape) return
    const current = displayShape.settings.spacing ?? 0
    const next = mode === 'expand'
      ? Math.min(MAX_SPACING, current + SPACING_STEP)
      : Math.max(MIN_SPACING, current - SPACING_STEP)
    if (next === current) return
    dispatch(updateShape(displayShape.id, { settings: { ...displayShape.settings, spacing: next } }))
  }, [displayShape, dispatch])

  const onFooterMouseUp = useCallback((e: unknown) => {
    // Edge-creation cancellation is handled by Graph.tsx's document-level mouseup/touchend
    // handler, which hit-tests the actual release position. Doing it here too is unsafe on
    // touch devices: touchend is captured to the original target (CreateEdgeControl, inside
    // this footer), so it would fire even when the finger is released over a node/canvas.
    if (!objectDetails.isOpen) return
    const control = html.getEventTarget(toHtmlEvent(e), (el: Element) => el.getAttribute('data-graph-type') === 'control')
    if (control) return
    dispatch(closeObjectDetails(false))
    ;(e as PointerEventData).preventDefault?.()
  }, [objectDetails.isOpen, dispatch])

  const openGenerateModal = useCallback(() => {
    if (isFetchingAny) return
    dispatch(openContentGenerationModal())
  }, [isFetchingAny, dispatch])

  const openAskModal = useCallback(() => {
    if (isFetchingAny) return
    dispatch(openGraphQueryModal())
  }, [isFetchingAny, dispatch])

  const handleCancel = useCallback(() => {
    dispatch(cancelContentGeneration())
  }, [dispatch])

  const handleCancelQuery = useCallback(() => {
    dispatch(cancelGraphQuery())
  }, [dispatch])

  return (
    <div className={cn('footer', { creating: creating.isCreating })} data-graph-type="footer" onMouseUp={onFooterMouseUp} onTouchEnd={onFooterMouseUp}>
      <ContentGenerationModal />
      <GraphQueryModal />
      <div className="controls">
        <div
          className="controls-main"
          style={{ transform: openMenu ? `translateX(-${openMenuWidth}px)` : undefined }}
        >
          {focusedEntity ? <KindControl kind={focusedKind} onClick={handleToggleKindMenu} showColorDot /> : null}
          <div className="controls-section">
            {contentGeneration?.isFetching ? (
              <>
                <Cancel
                  onClick={handleCancel}
                  control=""
                />
                <Spinner show={true} />
              </>
            ) : null}
            {graphQuery?.isFetching ? (
              <>
                <Cancel
                  onClick={handleCancelQuery}
                  control=""
                />
                <Spinner show={true} />
              </>
            ) : null}
            {graphQuery?.isFetching ? (
              <div style={{ float: 'left', paddingLeft: '0.5em' }}>
                {graphQuery?.status}
              </div>
            ) : null}
            {contentGeneration?.isFetching ? (
              <div style={{ float: 'left', paddingLeft: '0.5em' }}>
                {contentGeneration?.status}
              </div>
            ) : null}
          </div>
          <div className="controls-section">
            {edit.isEditing ? <Save closeObjectDetails={(v: unknown) => dispatch(closeObjectDetails(v as boolean | undefined))} control="<enter>" /> : null}
            {objectDetails.isOpen ? <Cancel onClick={() => dispatch(closeObjectDetails(false))} control="<esc>" /> : null}
          </div>
          <ContextualMenuToggle open={openMenu === 'graph'} onClick={handleToggleGraphMenu} icon={<FaProjectDiagram aria-hidden />} label="Graph editing menu" />
          <ContextualMenuToggle open={openMenu === 'content'} onClick={handleToggleContentMenu} icon={<FaRobot aria-hidden />} label="Content menu" />
          <ContextualMenuToggle open={openMenu === 'shape'} onClick={handleToggleShapeMenu} icon={<FaBars aria-hidden />} label="Shape menu" />
        </div>
        {openMenu ? <div className="controls-overlay" onClick={closeMenu} /> : null}
        {focusedEntity ? (
          <KindMenu
            open={openMenu === 'kind'}
            onCollapse={closeMenu}
            onWidthChange={handleKindMenuWidthChange}
            kind={focusedKind}
            kinds={kinds}
            objectType={focus.isEdge ? 'edge' : 'node'}
            title={kindMenuTitle}
            onChangeKindColor={handleChangeKindColor}
            onToggleEdgeVisible={handleToggleEdgeVisible}
            onToggleEdgeSpring={handleToggleEdgeSpring}
            onCycleEdgeDirection={handleCycleEdgeDirection}
            onToggleEdgeDirectionSoft={handleToggleEdgeDirectionSoft}
            onChangeEdgeStiffness={handleChangeEdgeStiffness}
            onChangeEdgeLength={handleChangeEdgeLength}
            onChangeEdgeThickness={handleChangeEdgeThickness}
            onSelectKind={handleSelectKind}
            editOneKind={(payload) => dispatch(editOneKind(payload) as any)}
            createOneKind={(payload) => dispatch(createOneKind(payload) as any)}
          />
        ) : null}
        {!edit.isEditing ? (
          <GraphEditMenu
            open={openMenu === 'graph'}
            onCollapse={closeMenu}
            onWidthChange={handleGraphMenuWidthChange}
            isCreating={creating.isCreating}
            isEditing={edit.isEditing}
            isFetchingAny={isFetchingAny}
            focus={focus}
            selected={selected}
            pinned={pinned}
            activeNodes={activeNodes}
            editableFocus={editableFocus}
            editableActive={editableActive}
            editableActiveEdges={editableActiveEdges}
            objectDetailsOpen={objectDetails.isOpen}
            onAddNode={() => dispatch(createAndFocusNode())}
            onAddConnectedNode={() => dispatch(createNodeFromActive())}
            onStartCreatingEdge={() => dispatch(startCreatingEdge())}
            onStopCreatingEdge={() => dispatch(stopCreatingEdge())}
            onToggleDirectionality={() => dispatch(toggleDirectionalityActive())}
            onDelete={() => dispatch(deleteActive())}
            onOpenObjectDetails={() => dispatch(openObjectDetails())}
            onTogglePin={() => dispatch(togglePinActive())}
            onToggleSelect={() => dispatch(toggleSelectFocus())}
            onDeselectAll={() => dispatch(deselectAll())}
          />
        ) : null}
        {!edit.isEditing ? (
          <ContentMenu
            open={openMenu === 'content'}
            onCollapse={closeMenu}
            onWidthChange={handleContentMenuWidthChange}
            isFetchingAny={isFetchingAny}
            isFailed={contentGeneration?.isFailed}
            hasFocus={!!focus.id}
            onGenerate={openGenerateModal}
            onAsk={openAskModal}
            onClear={() => dispatch(reset())}
          />
        ) : null}
        {!edit.isEditing && focus.id ? (
          <ShapeMenu
            open={openMenu === 'shape'}
            onCollapse={closeMenu}
            focusId={displayFocusId}
            shape={displayShape}
            isAdding={!!addingToShapeId}
            onAddShape={handleAddShape}
            onStartAddToShape={handleStartAddToShape}
            onCancelAddToShape={handleCancelAddToShape}
            onRemoveShape={handleRemoveShape}
            onMoveEntity={handleMoveEntity}
            onRemoveFromShape={handleRemoveFromShape}
            onToggleDirection={handleToggleDirection}
            onAdjustSpacing={handleAdjustSpacing}
            onWidthChange={handleShapeMenuWidthChange}
          />
        ) : null}
      </div>
    </div>
  )
}

export default Footer
